/**
 * @fileoverview Banner, audience, and active switch for the B2B event form.
 * @module pages/b2bEventForm/B2BEventBannerSection
 */

import { useCallback } from "react";
import { Switch } from "@mui/material";
import { generateAssetUrl } from "../../utils/common.js";
import AssetImage from "../../components/AssetImage";
import { ReactComponent as UploadIcon } from "../../assets/icons/Cloud_Upload.svg";
import styles from "../../styles/B2BEventForm.module.css";
import { isNoSociosAudience, isSociosAudience } from "./b2bEventFormModel";

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const AudienceCheckbox = ({ checked, onChange, label }) => (
  <label className={styles.audienciaCheckboxLabel}>
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span>{label}</span>
  </label>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const B2BEventBannerSection = ({
  t,
  formData,
  isEditMode,
  error,
  imageRef,
  onDragEnter,
  onDragLeave,
  onDrop,
  onImageChange,
  onAudienciaChange,
  onActivoChange,
}) => {
  const onSociosChange = useCallback(
    () => onAudienciaChange("socios"),
    [onAudienciaChange],
  );
  const onNoSociosChange = useCallback(
    () => onAudienciaChange("noSocios"),
    [onAudienciaChange],
  );

  return (
  <div className={styles.formGroupOneLeftStack}>
    <div className={styles.imageContainer}>
      <label className={styles.label}>{t("pages.b2bEventForm.eventBanner")}</label>
      <div
        ref={imageRef}
        className={styles.dropFileInput}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {isEditMode || formData.imagenBanner ? (
          <AssetImage
            className={styles.uploadedImage}
            src={generateAssetUrl(formData.imagenBanner)}
            alt={t("common.bannerAlt")}
          />
        ) : (
          <div className={styles.uploadContainer}>
            <UploadIcon className={styles.uploadIcon} />
            {error && <span className={styles.errorMessage}>{error}</span>}
            <span className={styles.uploadTitle}>
              {t("pages.b2bEventForm.drag&drop")}
            </span>
            <span className={styles.uploadSubtitle}>
              {t("pages.b2bEventForm.yourImage")}{" "}
              <span style={{ color: "#1D5AE8" }} className={styles.uploadSubtitle}>
                {t("pages.b2bEventForm.browseFile")}
              </span>
            </span>
          </div>
        )}
        <input
          className={styles.imageInput}
          type="file"
          accept="image/*"
          onChange={onImageChange}
        />
      </div>
    </div>
    <div className={styles.audienciaBelowBanner}>
      <label className={styles.label}>{t("pages.b2bEventForm.audience")}</label>
      <div className={styles.audienciaCheckboxRow}>
        <AudienceCheckbox
          checked={isSociosAudience(formData.audiencia)}
          onChange={onSociosChange}
          label={t("pages.b2bEventForm.audienceSocios")}
        />
        <AudienceCheckbox
          checked={isNoSociosAudience(formData.audiencia)}
          onChange={onNoSociosChange}
          label={t("pages.b2bEventForm.audienceNonMembers")}
        />
      </div>
    </div>
    <div className={styles.activoBelowBanner}>
      <label className={styles.label}>{t("pages.b2bEventForm.activateEvent")}</label>
      <Switch checked={Boolean(formData.activo)} onChange={onActivoChange} />
    </div>
  </div>
  );
};

export default B2BEventBannerSection;
